/**
 * Release Detail Page
 *
 * Detail page for viewing a Release QA run with tabs for testers, areas, regressions, and report
 */

import React from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Page, PageHeader, Text, Flex, Button, LoadingOrbit, KPICard, Tabs, TabList, Tab, TabPanel, useToast, TagBadge, badgeVariantToTagColor } from '@chatsdet/ui';
import {
  GitBranch,
  ArrowLeft,
  XCircle,
  Robot,
  Bug,
  CheckCircle,
  GitCommit,
} from '@phosphor-icons/react';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import {
  useReleaseRun,
  useCancelReleaseRun,
  useReleaseRunRegressions,
} from '../../features/releases/useReleases';
import type { ReleaseQARunStatus } from '../../features/releases/releaseTypes';
import { getReleaseRunStatusMeta, isActiveStatus } from '../../features/releases/releaseTypes';
import { ReleaseTestersList } from '../../features/releases/components/ReleaseTestersList';
import { ReleaseRegressionsList } from '../../features/releases/components/ReleaseRegressionsList';
import { ReleaseReport } from '../../features/releases/components/ReleaseReport';

const formatDuration = (
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined
): string => {
  if (!startedAt) return '-';
  const start = new Date(startedAt);
  const end = finishedAt ? new Date(finishedAt) : new Date();
  const durationMs = end.getTime() - start.getTime();

  if (durationMs < 1000) return '<1s';
  if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
  if (durationMs < 3600000) return `${Math.round(durationMs / 60000)}m`;
  return `${Math.round(durationMs / 3600000)}h`;
};

const formatRelativeTime = (date: string | null | undefined): string => {
  if (!date) return '-';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();

  if (diffMs < 60000) return 'just now';
  if (diffMs < 3600000) return `${Math.round(diffMs / 60000)}m ago`;
  if (diffMs < 86400000) return `${Math.round(diffMs / 3600000)}h ago`;
  return then.toLocaleDateString();
};

const RunStatusBadge: React.FC<{ status: ReleaseQARunStatus }> = ({ status }) => {
  const meta = getReleaseRunStatusMeta(status);
  const variantMap: Record<
    string,
    'success' | 'secondary' | 'warning' | 'destructive' | 'outline'
  > = {
    green: 'success',
    blue: 'secondary',
    yellow: 'warning',
    red: 'destructive',
    gray: 'outline',
  };
  return <TagBadge name={meta.label} color={badgeVariantToTagColor(variantMap[meta.color] ?? 'outline')} hideDot />;
};

export const ReleaseDetail: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { releaseId } = useParams({ from: '/releases/$releaseId' });
  const search = useSearch({ from: '/releases/$releaseId' });
  const tabFromSearch = (search as { tab?: string }).tab;

  const runQuery = useReleaseRun(releaseId);
  const cancelMutation = useCancelReleaseRun();
  // Same default args (page 1, pageSize 20, no status filter) ReleaseRegressionsList uses,
  // so this shares its React Query cache entry instead of firing a second request.
  const regressionsQuery = useReleaseRunRegressions(releaseId);

  const [selectedTab, setSelectedTab] = React.useState<
    'testers' | 'regressions' | 'report'
  >((tabFromSearch as 'testers' | 'regressions' | 'report') ?? 'testers');

  const run = runQuery.data?.run;

  useBreadcrumbs([
    { label: 'Releases', onPress: () => navigate({ to: '/releases' }) },
    { label: run?.title || run?.propertyName || 'Release Run' },
  ]);

  const handleCancel = async () => {
    if (!releaseId) return;
    try {
      await cancelMutation.mutateAsync(releaseId);
      toast.success('Release run canceled');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to cancel run');
    }
  };

  const handleTabChange = (key: React.Key) => {
    const tab = key as 'testers' | 'regressions' | 'report';
    setSelectedTab(tab);
    navigate({
      to: '/releases/$releaseId',
      params: { releaseId },
      search: { tab },
    });
  };

  // Loading state
  if (runQuery.isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      </Page>
    );
  }

  // Error state
  if (runQuery.isError || !run) {
    return (
      <Page>
        <PageHeader
          title="Release Run Not Found"
          description="The requested release run could not be found"
        />
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <Text className="text-muted-foreground">
            This release run may have been deleted or you may not have access.
          </Text>
          <Button
            variant="outline"
            size="sm"
            onPress={() => navigate({ to: '/releases' })}
            className="mt-4"
          >
            Back to Releases
          </Button>
        </div>
      </Page>
    );
  }

  const isRunning = isActiveStatus(run.status);

  const headerDescription = (
    <Flex align="center" gap="xs" wrap>
      <Text size="sm" className="text-muted-foreground">
        Duration {formatDuration(run.startedAt, run.finishedAt)}
      </Text>
      <Text size="sm" className="text-muted-foreground">
        /
      </Text>
      <Text size="sm" className="text-muted-foreground">
        Started {formatRelativeTime(run.startedAt ?? run.createdAt)}
      </Text>
      {run.fromSha && run.toSha && (
        <>
          <Text size="sm" className="text-muted-foreground">
            /
          </Text>
          <Text size="sm" className="text-muted-foreground font-mono">
            {run.fromSha.slice(0, 7)}...{run.toSha.slice(0, 7)}
          </Text>
        </>
      )}
      {run.cutoffReason && (
        <>
          <Text size="sm" className="text-muted-foreground">
            /
          </Text>
          <Text size="sm" className="text-muted-foreground">
            Stopped: {run.cutoffReason.replace(/_/g, ' ')}
          </Text>
        </>
      )}
    </Flex>
  );

  // The regressionTests* columns on `run` are written once by the compile-report job and never
  // revisited, so they go stale the moment a retry lands (e.g. "Regressions (5)" / "4 passed"
  // above five all-green rows). Prefer the live summary from the regressions endpoint, which is
  // recomputed from the latest attempt of every workflow every time it's fetched. While that
  // query is still in flight, fall back to the stored columns so the KPI shows a plausible
  // number instead of flashing 0.
  const regressionSummary = regressionsQuery.data?.summary;
  const regressionsTotal = regressionSummary?.total ?? run.regressionTestsTotal;
  const regressionsPassed = regressionSummary?.passed ?? run.regressionTestsPassed;
  const regressionsFailed = regressionSummary?.failed ?? run.regressionTestsFailed;

  const regressionPassRate =
    regressionsTotal > 0 ? Math.round((regressionsPassed / regressionsTotal) * 100) : null;

  return (
    <Page>
      <PageHeader
        icon={<GitBranch size={24} weight="fill" />}
        eyebrow={<RunStatusBadge status={run.status} />}
        title={run.title || run.propertyName}
        description={headerDescription}
        actions={
          <Flex gap="sm">
            <Button
              variant="outline"
              size="sm"
              onPress={() => navigate({ to: '/releases' })}
              iconLeft={<ArrowLeft size={16} />}
            >
              Back
            </Button>
            {isRunning && (
              <Button
                variant="destructive"
                size="sm"
                onPress={() => void handleCancel()}
                isDisabled={cancelMutation.isPending}
                iconLeft={<XCircle size={16} />}
              >
                {cancelMutation.isPending ? 'Canceling...' : 'Cancel Run'}
              </Button>
            )}
          </Flex>
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
        <KPICard
          title="Testers"
          value={run.testersSpawned}
          subtitle={`${run.testersCompleted} completed${run.testersFailed > 0 ? `, ${run.testersFailed} failed` : ''}`}
          icon={<Robot size={16} />}
          variant={run.testersFailed > 0 ? 'warning' : 'default'}
          size="sm"
        />
        <KPICard
          title="Issues Found"
          value={run.issuesFound}
          icon={<Bug size={16} />}
          variant={run.issuesFound > 0 ? 'destructive' : 'success'}
          size="sm"
        />
        <KPICard
          title="Regressions"
          value={regressionsTotal}
          subtitle={
            regressionsTotal > 0
              ? `${regressionsPassed} passed${regressionPassRate !== null ? ` (${regressionPassRate}%)` : ''}`
              : 'no workflows'
          }
          icon={<CheckCircle size={16} />}
          variant={regressionsFailed > 0 ? 'warning' : regressionsTotal > 0 ? 'success' : 'muted'}
          size="sm"
        />
        <KPICard
          title="Changes"
          value={run.commitsAnalyzed}
          subtitle="commits"
          icon={<GitCommit size={16} />}
          variant="muted"
          size="sm"
        />
      </div>

      {/* Tabs */}
      <div className="mt-6">
        <Tabs selectedKey={selectedTab} onSelectionChange={handleTabChange}>
          <TabList aria-label="Release run views">
            <Tab id="testers">Testers ({run.testersSpawned})</Tab>
            <Tab id="regressions">
              Regressions{regressionsTotal > 0 ? ` (${regressionsTotal})` : ''}
            </Tab>
            <Tab id="report">Release Notes</Tab>
          </TabList>

          <TabPanel id="testers">
            <ReleaseTestersList releaseRunId={releaseId} />
          </TabPanel>

          <TabPanel id="regressions">
            <ReleaseRegressionsList releaseRunId={releaseId} />
          </TabPanel>

          <TabPanel id="report">
            <ReleaseReport
              releaseRunId={releaseId}
              runStatus={run.status}
              releaseNotesMarkdown={run.releaseNotesMarkdown ?? null}
              releaseNotesCompletedAt={run.releaseNotesCompletedAt ?? null}
              commitsAnalyzed={run.commitsAnalyzed ?? 0}
            />
          </TabPanel>
        </Tabs>
      </div>
    </Page>
  );
};
